<template>
	<div class="cne">
		<p class="cne-hint">{{ copy.hint }}</p>
		<p v-if="blockTotal" class="cne-block-total">
			Ajuste total del bloque (últimos doce meses):
			<strong :class="{ 'is-residual': blockTotal.residual_total !== 0 }">{{ money(blockTotal.residual_total) }}</strong>.
			Es lo que muestra el reporte: la unión de las cuentas de todos los neteos, no la suma de sus residuos.
		</p>

		<p v-if="nettings.length === 0 && !editing" class="cne-empty">
			Sin neteos de {{ copy.block }} todavía. Crea el primero con el botón de abajo.
		</p>

		<ul v-else-if="nettings.length" class="cne-list">
			<li v-for="netting in nettings" :key="netting.id" class="cne-item">
				<div class="cne-item__title">
					<span class="cne-label">{{ labelOf(netting) }}</span>
					<span class="cne-meta">{{ metaOf(netting) }}</span>
					<span v-if="sharesWith(netting)" class="cne-meta cne-meta--shares">
						Comparte cuentas con {{ sharesWith(netting) }}
					</span>
				</div>
				<div class="cne-sides">
					<div class="cne-side">
						<span class="cne-side__title">{{ copy.left }}</span>
						<span v-for="line in linesOf(netting, 'left')" :key="line.id" class="cne-line">
							<strong>{{ line.member_name }}</strong> · {{ line.level_3 }}
						</span>
					</div>
					<div class="cne-side cne-side--right">
						<span class="cne-side__title">{{ copy.right }}</span>
						<span v-for="line in linesOf(netting, 'right')" :key="line.id" class="cne-line">
							<strong>{{ line.member_name }}</strong> · {{ line.level_3 }}
						</span>
					</div>
				</div>
				<div class="cne-item__actions">
					<template v-if="confirmingDelete === netting.id">
						<span class="cne-confirm">¿Eliminar este neteo? El reporte vuelve a mostrar sus cuentas.</span>
						<button type="button" class="theme_btn_red" :disabled="saving" @click="$emit('delete', netting)">
							Confirmar
						</button>
						<button type="button" class="theme_btn_white" :disabled="saving" @click="confirmingDelete = null">
							Cancelar
						</button>
					</template>
					<template v-else>
						<button type="button" class="theme_btn_white" :disabled="saving || !!editing" @click="edit(netting)">
							Editar
						</button>
						<button type="button" class="theme_btn_white" :disabled="saving || !!editing" @click="confirmingDelete = netting.id">
							Eliminar
						</button>
					</template>
				</div>
			</li>
		</ul>

		<div v-if="!editing" class="cne-new">
			<button type="button" class="theme_btn_blue" :disabled="saving" @click="startNew">
				Nuevo neteo de {{ copy.block }}
			</button>
		</div>

		<!-- Cuenta T: los dos lados enfrentados y, en el eje, el residuo. -->
		<form v-else class="cne-form" @submit.prevent="save">
			<div class="cne-form__label">
				<label :for="labelId" class="cne-field__label">Nombre (opcional)</label>
				<input :id="labelId" v-model="form.label" type="text" class="cne-input" maxlength="64"
					placeholder="Por ejemplo: Cuentas relacionadas" />
			</div>

			<div class="cne-taccount">
				<fieldset v-for="(side, index) in sides" :key="side.key" class="cne-fieldset" :class="'cne-fieldset--' + side.key">
					<legend class="cne-field__label">{{ side.title }}</legend>
					<ul class="cne-chosen">
						<li v-if="form[side.key].length === 0" class="cne-chosen__empty">Sin cuentas todavía</li>
						<li v-for="(line, lineIndex) in form[side.key]" :key="line.member_company_id + ':' + line.account_id" class="cne-chosen__item">
							<span class="cne-chosen__text">
								<strong>{{ line.member_name }}</strong> · {{ line.level_3 }}
								<span v-if="isReused(line)" class="cne-chip">saldo restante</span>
							</span>
							<button type="button" class="cne-remove" :aria-label="'Quitar ' + line.level_3"
								@click="removeLine(side.key, lineIndex)">×</button>
						</li>
					</ul>
					<div class="cne-add">
						<!-- Mismo selector que las cuentas ignoradas (MultiSelectDropdown
							en selección única): empresa primero y, con ella elegida, la
							cuenta con buscador. -->
						<MultiSelectDropdown
							class="cne-pick cne-pick--member"
							:value="picked(draft[side.key].member)"
							:options="memberOptions(side.key)"
							:placeholder="memberPlaceholder(side.key)"
							single
							clear-label="Quitar"
							@input="pickMember(side.key, $event)"
						/>
						<MultiSelectDropdown
							class="cne-pick cne-pick--account"
							:value="picked(draft[side.key].account)"
							:options="accountOptions(side.key)"
							:placeholder="accountPlaceholder(side.key)"
							:disabled="draft[side.key].member === null"
							single
							searchable
							search-placeholder="Buscar cuenta…"
							:label-max-len="120"
							clear-label="Quitar"
							@input="pickAccount(side.key, $event)"
						/>
						<button v-if="draft[side.key].account !== null" type="button" class="theme_btn_white cne-add__btn"
							@click="addLine(side.key)">
							Agregar
						</button>
					</div>
					<div v-if="index === 0" class="cne-spine" aria-hidden="true">
						<span class="cne-spine__badge" :class="{ 'is-residual': residualTotal !== null && residualTotal !== 0 }">
							<template v-if="residualTotal === null">neteo</template>
							<template v-else>
								<span class="cne-spine__word">ajuste</span>{{ money(residualTotal) }}
							</template>
						</span>
					</div>
				</fieldset>
			</div>

			<div v-if="hasPreview" class="cne-preview">
				<p class="cne-field__label">Últimos doce meses (informativo, devengado de cada mes)</p>
				<ul v-if="reusedAccounts.length" class="cne-legend">
					<li v-for="account in reusedAccounts" :key="account.member_company_id + ':' + account.account_id">
						<span class="cne-chip">saldo restante</span>
						<strong>{{ account.member_name }}</strong> · {{ account.level_3 }}:
						{{ money(account.original_total) }} → {{ money(account.remaining_total) }}
						(ya en {{ refsLabel(account.used_in) }})
					</li>
				</ul>
				<table class="cne-table">
					<thead>
						<tr>
							<th scope="col">Mes</th>
							<th scope="col" class="is-num">{{ copy.left }}</th>
							<th scope="col" class="is-num">{{ copy.right }}</th>
							<th scope="col" class="is-num">Ajuste</th>
						</tr>
					</thead>
					<tbody>
						<tr v-if="preview.months.length === 0">
							<td colspan="4" class="cne-muted">Sin movimientos en estas cuentas en el último año.</td>
						</tr>
						<tr v-for="row in preview.months" :key="row.period">
							<td>{{ monthLabel(row.period) }}</td>
							<td class="is-num">{{ money(row.left) }}</td>
							<td class="is-num">{{ money(row.right) }}</td>
							<td class="is-num" :class="{ 'is-residual': row.residual !== 0 }">{{ money(row.residual) }}</td>
						</tr>
					</tbody>
					<tfoot v-if="preview.months.length">
						<tr>
							<th scope="row">Total</th>
							<td class="is-num">{{ money(preview.left_total) }}</td>
							<td class="is-num">{{ money(preview.right_total) }}</td>
							<td class="is-num">{{ money(preview.residual_total) }}</td>
						</tr>
					</tfoot>
				</table>
				<p class="cne-muted">
					El ajuste es {{ copy.right }} menos {{ copy.left }} de cada mes y se muestra en la línea
					«Ajustes intercompañía» de {{ copy.target }}.
				</p>
			</div>
			<p v-else-if="previewLines.length === 0" class="cne-muted">
				Elige una cuenta para ver sus últimos meses. Para guardar necesitas al menos una cuenta
				de cada lado.
			</p>

			<div class="cne-actions">
				<button type="submit" class="theme_btn_dark" :disabled="saving || !isComplete">
					{{ form.adjustmentId ? "Guardar cambios" : "Guardar neteo" }}
				</button>
				<button type="button" class="theme_btn_white" :disabled="saving" @click="cancel">
					Cancelar
				</button>
			</div>
		</form>
	</div>
</template>

<script>
// Editor de neteos de un bloque (Balance o Resultados) de la pestaña
// «Consolidación» (plan consolidadas, D1/D3/D4/D7). Presentacional: recibe
// los neteos del bloque, las opciones por lado y el preview; emite
// `load-options` al abrir el formulario, `preview` con cada cambio de las
// cuentas en juego (las agregadas más la elegida en el select, aunque no se
// haya agregado: los meses se ven apenas se elige), `save` y `delete`. El
// padre habla con el store y notifica; el título del bloque lo lleva la
// tarjeta plegable que lo contiene. El formulario es una cuenta T: los dos
// lados enfrentados y el residuo en el eje.
import MultiSelectDropdown from "@/components/generics/MultiSelectDropdown";
import { parseToMoney } from "@/utils/currency";
import { formatMonthLabel } from "@/utils/dateLabels";

const COPY = {
	netting_balance: {
		block: "balance",
		hint: "Izquierda: cuentas de Activos de un miembro. Derecha: cuentas de Pasivos de otro. "
			+ "Las cuentas salen del reporte y el residuo mensual va a «Ajustes intercompañía» en Patrimonio.",
		left: "Activos",
		right: "Pasivos",
		target: "Patrimonio",
	},
	netting_results: {
		block: "resultados",
		hint: "Izquierda: cuentas de Ventas de un miembro. Derecha: cuentas de Costos de otro. "
			+ "Las cuentas salen del reporte y el residuo mensual va a «Ajustes intercompañía» en Gastos No Operacionales.",
		left: "Ventas",
		right: "Costos",
		target: "Gastos No Operacionales",
	},
};

const emptyForm = () => ({ adjustmentId: null, label: "", left: [], right: [] });
const emptyDraft = () => ({ left: { member: null, account: null }, right: { member: null, account: null } });

export default {
	name: "ConsolidatedNettingEditor",
	components: {
		MultiSelectDropdown,
	},
	props: {
		kind: {
			type: String,
			required: true,
			validator: (value) => Object.keys(COPY).includes(value),
		},
		nettings: {
			type: Array,
			default: () => [],
		},
		// { left: [{ company_id, name, accounts }], right: [...] }
		options: {
			type: Object,
			default: () => ({ left: [], right: [] }),
		},
		preview: {
			type: Object,
			default: null,
		},
		saving: {
			type: Boolean,
			default: false,
		},
		// { left_total, right_total, residual_total, ... } del bloque (D14) o null.
		blockTotal: {
			type: Object,
			default: null,
		},
	},
	data() {
		return {
			editing: false,
			confirmingDelete: null,
			form: emptyForm(),
			draft: emptyDraft(),
		};
	},
	computed: {
		copy() {
			return COPY[this.kind];
		},
		labelId() {
			return `cne-label-${this.kind}`;
		},
		sides() {
			return [
				{ key: "left", title: `Lado izquierdo · ${this.copy.left}` },
				{ key: "right", title: `Lado derecho · ${this.copy.right}` },
			];
		},
		// Guardar exige una cuenta en juego por lado (agregada o elegida en
		// el select): lo que se ve en la tabla es lo que se guarda.
		isComplete() {
			return ["left", "right"].every((side) => this.form[side].length > 0 || this.draftLine(side) !== null);
		},
		// Lo agregado más la cuenta elegida en cada select (si sigue en las
		// opciones): es lo que previsualiza la tabla mensual.
		previewLines() {
			return ["left", "right"].flatMap((side) => {
				const lines = this.form[side].map((line) => ({
					member_company_id: line.member_company_id,
					account_id: line.account_id,
					side,
				}));
				const pending = this.draftLine(side);
				if (!pending) return lines;
				return [...lines, { member_company_id: pending.member_company_id, account_id: pending.account_id, side }];
			});
		},
		previewKey() {
			return JSON.stringify(this.previewLines);
		},
		hasPreview() {
			return !!this.preview && this.previewLines.length > 0;
		},
		// Residuo del preview vigente para el eje de la cuenta T (null sin preview).
		residualTotal() {
			if (!this.hasPreview) return null;
			return Number(this.preview.residual_total || 0);
		},
		// Cuentas del preview que ya están en neteos anteriores: su monto es
		// el saldo restante que dejan esos neteos (D12/D13).
		reusedAccounts() {
			if (!this.hasPreview) return [];
			return (this.preview.accounts || []).filter((account) => account.used_in && account.used_in.length);
		},
	},
	watch: {
		// Cada cambio en las cuentas en juego refresca la tabla informativa
		// (la clave serializada evita repetir el pedido cuando una cuenta pasa
		// del select a la lista de agregadas).
		previewKey() {
			if (this.editing && this.previewLines.length > 0) {
				this.$emit("preview", {
					kind: this.kind,
					lines: this.previewLines,
					adjustmentId: this.form.adjustmentId,
				});
			}
		},
	},
	methods: {
		money: parseToMoney,
		monthLabel(period) {
			return formatMonthLabel(String(period || "").slice(0, 7));
		},
		labelOf(netting) {
			return netting.label || `Neteo ${netting.id}`;
		},
		metaOf(netting) {
			return netting.created_by ? `Guardado por ${netting.created_by}` : "";
		},
		linesOf(netting, side) {
			return netting.lines.filter((line) => line.side === side);
		},
		// «N1», «N2» (nombre o «Neteo id») de una lista de referencias.
		refsLabel(refs) {
			return (refs || []).map((ref) => `«${this.labelOf(ref)}»`).join(", ");
		},
		sharesWith(netting) {
			return this.refsLabel(netting.shares_with);
		},
		optionLabel(account) {
			const base = `${account.level_3} · ${account.level_2}`;
			return account.used_in && account.used_in.length ? `${base} · en ${this.refsLabel(account.used_in)}` : base;
		},
		// Puente con MultiSelectDropdown (v-model de ids): el borrador guarda un
		// id o null; el dropdown habla en `[id]` / `[]`.
		picked(id) {
			return id === null ? [] : [id];
		},
		memberOptions(side) {
			return this.optionsFor(side).map((row) => ({ id: row.company_id, label: row.name }));
		},
		accountOptions(side) {
			return this.accountsFor(side).map((account) => ({ id: account.id, label: this.optionLabel(account) }));
		},
		memberPlaceholder(side) {
			const row = this.optionsFor(side).find((option) => option.company_id === this.draft[side].member);
			return row ? row.name : "Empresa";
		},
		accountPlaceholder(side) {
			const { member, account } = this.draft[side];
			const row = this.optionsFor(side).find((option) => option.company_id === member);
			const chosen = row && row.accounts.find((option) => option.id === account);
			return chosen ? this.optionLabel(chosen) : "Cuenta";
		},
		pickMember(side, ids) {
			this.draft[side].member = ids.length ? ids[0] : null;
			this.draft[side].account = null;
		},
		pickAccount(side, ids) {
			this.draft[side].account = ids.length ? ids[0] : null;
		},
		isReused(line) {
			return this.reusedAccounts.some(
				(account) => account.member_company_id === line.member_company_id && account.account_id === line.account_id,
			);
		},
		optionsFor(side) {
			return this.options[side] || [];
		},
		accountsFor(side) {
			const memberId = this.draft[side].member;
			const row = this.optionsFor(side).find((option) => option.company_id === memberId);
			const chosen = new Set(this.form[side].map((line) => `${line.member_company_id}:${line.account_id}`));
			return (row ? row.accounts : []).filter((account) => !chosen.has(`${memberId}:${account.id}`));
		},
		startNew() {
			this.form = emptyForm();
			this.draft = emptyDraft();
			this.editing = true;
			this.$emit("load-options", { kind: this.kind, adjustmentId: null });
		},
		edit(netting) {
			this.form = {
				adjustmentId: netting.id,
				label: netting.label || "",
				left: this.linesOf(netting, "left").map(this.lineModel),
				right: this.linesOf(netting, "right").map(this.lineModel),
			};
			this.draft = emptyDraft();
			this.editing = true;
			this.$emit("load-options", { kind: this.kind, adjustmentId: netting.id });
		},
		lineModel(line) {
			return {
				member_company_id: line.member_company_id,
				member_name: line.member_name,
				account_id: line.account_id,
				level_3: line.level_3,
			};
		},
		// La cuenta elegida en el select de `side`, resuelta contra las
		// opciones; null si falta la empresa o la cuenta ya no se ofrece.
		draftLine(side) {
			const { member, account } = this.draft[side];
			const row = this.optionsFor(side).find((option) => option.company_id === member);
			const chosen = row && row.accounts.find((option) => option.id === account);
			if (!row || !chosen) return null;
			return {
				member_company_id: member,
				member_name: row.name,
				account_id: account,
				level_3: chosen.level_3,
			};
		},
		addLine(side) {
			const pending = this.draftLine(side);
			if (!pending) return;
			this.form[side].push(pending);
			this.draft[side].account = null;
		},
		removeLine(side, index) {
			this.form[side].splice(index, 1);
		},
		// Guarda las cuentas en juego: una elegida en el select sin Agregar
		// también entra (es la que muestra la tabla).
		save() {
			if (!this.isComplete) return;
			this.$emit("save", {
				kind: this.kind,
				label: this.form.label.trim(),
				lines: this.previewLines,
				adjustmentId: this.form.adjustmentId,
			});
		},
		cancel() {
			this.editing = false;
			this.form = emptyForm();
			this.draft = emptyDraft();
			this.$emit("cancel", { kind: this.kind });
		},
		// El padre cierra el formulario cuando el guardado termina bien.
		close() {
			this.cancel();
		},
	},
};
</script>

<style lang="scss" scoped>
@use "./account_plan_buttons" as *;

.cne {
	display: flex;
	flex-direction: column;
	gap: 14px;
	padding-top: 12px;
	min-width: 0;
}

.cne-hint,
.cne-muted {
	margin: 0;
	max-width: 76ch;
	font-size: 13px;
	line-height: 1.5;
	color: var(--ap-slate);
}

.cne-empty {
	margin: 0;
	padding: 14px 16px;
	border: 1px dashed var(--ap-line-strong);
	border-radius: var(--ap-radius-sm);
	font-size: 13.5px;
	color: var(--ap-slate);
}

// Neteos existentes: una fila por neteo, los dos lados enfrentados ------------
.cne-list {
	margin: 0;
	padding: 0;
	list-style: none;
}

.cne-item {
	display: grid;
	grid-template-columns: minmax(140px, 1fr) minmax(0, 2fr) auto;
	gap: 16px;
	align-items: start;
	padding: 12px 0;
	border-top: 1px solid var(--ap-line);
}

.cne-block-total {
	margin: 0 0 12px;
	font-size: 13px;
	color: var(--ap-slate);

	strong {
		color: var(--ap-ink);

		&.is-residual {
			color: var(--ap-warn-ink);
		}
	}
}

.cne-chip {
	display: inline-block;
	margin-left: 6px;
	padding: 1px 7px;
	border: 1px solid var(--ap-warn);
	border-radius: 999px;
	background: var(--ap-warn-wash);
	font-size: 11px;
	font-weight: 600;
	line-height: 16px;
	color: var(--ap-warn-ink);
	vertical-align: middle;
}

.cne-legend {
	margin: 0 0 10px;
	padding: 0;
	list-style: none;
	font-size: 12.5px;
	color: var(--ap-slate);

	li {
		margin: 2px 0;
	}

	.cne-chip {
		margin: 0 6px 0 0;
	}
}

.cne-meta--shares {
	color: var(--ap-warn-ink);
}

.cne-label {
	display: block;
	font-weight: 600;
	color: var(--ap-ink);
}

.cne-meta {
	display: block;
	font-size: 12px;
	color: var(--ap-muted);
}

.cne-sides {
	display: grid;
	grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
	gap: 0 12px;
	min-width: 0;
}

.cne-side {
	display: flex;
	flex-direction: column;
	gap: 2px;
	min-width: 0;
	font-size: 13px;

	&--right {
		padding-left: 12px;
		border-left: 1px solid var(--ap-line-strong);
	}

	&__title {
		font-size: 11px;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.08em;
		color: var(--ap-muted);
	}
}

.cne-line {
	overflow-wrap: anywhere;
}

.cne-item__actions {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	align-items: center;
	justify-content: flex-end;
}

.cne-confirm {
	font-size: 12.5px;
	color: var(--ap-alert);
}

// Formulario: cuenta T -------------------------------------------------------
.cne-form {
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding-top: 14px;
	border-top: 1px solid var(--ap-line);
	min-width: 0;
}

.cne-field__label {
	display: block;
	margin: 0 0 6px;
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.08em;
	color: var(--ap-muted);
}

.cne-input {
	width: 100%;
	min-width: 0;
	height: 36px;
	padding: 0 10px;
	border: 1px solid var(--ap-line-strong);
	border-radius: var(--ap-radius-sm);
	background: var(--ap-paper);
	font-family: var(--ap-font);
	font-size: 13.5px;
	color: var(--ap-ink-2);
	text-overflow: ellipsis;

	&:focus-visible {
		outline: 2px solid var(--ap-teal);
		outline-offset: 1px;
	}

	&:disabled {
		color: var(--ap-muted);
		background: var(--ap-mist);
	}
}

.cne-input {
	width: min(100%, 360px);
}

// Las dos columnas de la T comparten un eje central donde se asienta el
// residuo del preview; cada columna puede encogerse (minmax 0).
.cne-taccount {
	position: relative;
	display: grid;
	grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
	gap: 0 48px;
	min-width: 0;
	margin-top: 10px;
}

.cne-fieldset {
	position: relative;
	margin: 0;
	padding: 14px 0 6px;
	border: 0;
	border-top: 1px solid var(--ap-line-strong);
	min-width: 0;

	legend {
		padding: 0 8px 0 0;
		margin-bottom: 6px;
	}

	&--right legend {
		margin-left: auto;
		text-align: right;
		padding: 0 0 0 8px;
	}
}

// El eje de la T: baja por el hueco entre las dos columnas y, sobre la
// barra superior, lleva el residuo del preview (o «neteo» hasta que lo hay).
// Trazos finos y grises. El eje NO lleva z-index (crearía un contexto de
// apilamiento que encerraría al badge); el badge sí, y alto: el ajuste y su
// fondo quedan siempre por encima de barras, selects y chips del formulario.
.cne-spine {
	position: absolute;
	top: 0;
	right: -24px;
	bottom: 0;
	width: 0;
	border-left: 1px solid var(--ap-line-strong);
	pointer-events: none;

	&__badge {
		position: absolute;
		top: -1px;
		left: 0;
		transform: translate(-50%, -50%);
		z-index: 5;
		display: inline-flex;
		align-items: baseline;
		gap: 6px;
		padding: 4px 12px;
		border-radius: var(--ap-radius-pill);
		background: var(--ap-paper);
		border: 1px solid var(--ap-line-strong);
		font-size: 11px;
		font-weight: 700;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--ap-ink);
		white-space: nowrap;
		font-variant-numeric: tabular-nums;
		pointer-events: auto;

		&.is-residual {
			text-transform: none;
			letter-spacing: 0;
			font-size: 13px;
			border-color: var(--ap-warn);
			// El wash es translúcido (dejaba ver las barras): va como capa
			// sobre el papel para que el fondo sea opaco sin acuñar un hex.
			background: linear-gradient(var(--ap-warn-wash), var(--ap-warn-wash)), var(--ap-paper);
			color: var(--ap-warn-ink);
		}
	}

	&__word {
		font-size: 10.5px;
		font-weight: 600;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--ap-slate);
	}
}

.cne-chosen {
	margin: 0 0 10px;
	padding: 0;
	list-style: none;
	display: flex;
	flex-direction: column;
	gap: 4px;
	min-height: 28px;

	&__empty {
		font-size: 13px;
		color: var(--ap-muted);
	}

	&__item {
		display: flex;
		justify-content: space-between;
		align-items: baseline;
		gap: 8px;
		padding: 4px 0;
		border-bottom: 1px solid var(--ap-line);
		font-size: 13px;
		min-width: 0;
	}

	&__text {
		min-width: 0;
		overflow-wrap: anywhere;
	}
}

.cne-remove {
	appearance: none;
	flex: 0 0 auto;
	border: 0;
	background: transparent;
	color: var(--ap-alert);
	font-size: 16px;
	line-height: 1;
	cursor: pointer;

	&:focus-visible {
		outline: 2px solid var(--ap-teal);
		outline-offset: 2px;
	}
}

// Alta de una línea: la empresa arriba a todo el ancho; la cuenta y el
// botón debajo (dos selects en una fila se truncaban en media columna).
.cne-add {
	display: grid;
	grid-template-columns: minmax(0, 1fr) auto;
	grid-template-areas:
		"member member"
		"account btn";
	gap: 8px;
	min-width: 0;
}

.cne-pick--member {
	grid-area: member;
}

.cne-pick--account {
	grid-area: account;
}

// El dropdown genérico es inline (chip de filtro); acá ocupa la columna como
// un campo de formulario y el panel se alinea con él. Mismo look que en las
// cuentas ignoradas; sólo cambia el ancho. La única caja es el trigger del
// dropdown: el wrapper (.cne-pick) no lleva borde ni alto propios.
.cne-pick {
	display: block;
	width: 100%;
	min-width: 0;
}

.cne-add :deep(.ms_wrapper) {
	width: 100%;
}

.cne-add :deep(.ms_trigger) {
	width: 100%;
	min-height: 36px;
	justify-content: space-between;
}

.cne-add :deep(.ms_label) {
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.cne-add :deep(.ms_panel) {
	width: 100%;
	box-sizing: border-box;
}

.cne-add :deep(.ms_option_label) {
	white-space: normal;
	overflow-wrap: anywhere;
}

.cne-add__btn {
	grid-area: btn;
	white-space: nowrap;
}

// Tabla mensual: las columnas Activos / Pasivos siguen a las de la T ------------
.cne-preview {
	display: flex;
	flex-direction: column;
	gap: 8px;
	min-width: 0;
	overflow-x: auto;
	padding-top: 6px;
	border-top: 1px solid var(--ap-line);
}

.cne-table {
	width: 100%;
	border-collapse: collapse;
	font-size: 13px;

	th,
	td {
		padding: 8px 10px;
		text-align: left;
		border-bottom: 1px solid var(--ap-line);
		white-space: nowrap;
	}

	th {
		font-size: 11px;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.08em;
		color: var(--ap-muted);
	}

	.is-num {
		text-align: right;
		font-variant-numeric: tabular-nums;
	}

	.is-residual {
		color: var(--ap-warn-ink);
	}

	tfoot td,
	tfoot th {
		border-bottom: 0;
		font-weight: 700;
		color: var(--ap-ink);
	}
}

.cne-actions {
	display: flex;
	gap: 8px;
}

// Panel angosto: la T se apila y el eje pasa a ser horizontal.
@media (max-width: 1100px) {
	.cne-taccount {
		grid-template-columns: minmax(0, 1fr);
		gap: 28px;
	}

	.cne-spine {
		top: auto;
		right: 0;
		left: 0;
		bottom: -15px;
		width: auto;
		height: 0;
		border-left: 0;
		border-top: 1px solid var(--ap-line-strong);

		&__badge {
			top: 0;
			left: 50%;
		}
	}

	.cne-fieldset--right legend {
		margin-left: 0;
		text-align: left;
	}
}

@media (max-width: 860px) {
	.cne-item,
	.cne-sides,
	.cne-add {
		grid-template-columns: minmax(0, 1fr);
	}

	.cne-side--right {
		padding-left: 0;
		border-left: 0;
		margin-top: 8px;
	}
}
</style>
